import { useState } from "react";
import { cancelReview, finalizeReview } from "./api";
import type { ReviewDelta } from "./schema";
import { useConfirm } from "../ui/ConfirmDialog";

interface Props {
  sessionId: string;
  onCancel: () => void;
  onFinalized: (newVersion: number) => void;
}

export function ReviewDeltaScreen({ sessionId, onCancel, onFinalized }: Props) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState<"finalize" | "cancel" | null>(null);
  const [delta, setDelta] = useState<ReviewDelta | null>(null);
  const [newVersion, setNewVersion] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFinalize = async () => {
    setBusy("finalize");
    setError(null);
    try {
      const r = await finalizeReview(sessionId);
      setDelta(r.delta);
      setNewVersion(r.newVersion);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    const ok = await confirm({
      title: "Cancel this review?",
      message: "Any edits you've made in this review will be discarded. The current canvas version stays unchanged.",
      confirmLabel: "Discard edits",
      cancelLabel: "Keep editing",
      danger: true,
    });
    if (!ok) return;
    setBusy("cancel");
    setError(null);
    try {
      await cancelReview(sessionId);
      onCancel();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };

  if (delta && newVersion !== null) {
    const sameGrade = delta.gradeDelta.previous === delta.gradeDelta.new;
    return (
      <div className="canvas">
        <div className="canvas__panel">
          <p className="canvas__eyebrow">Phase 10 — Review complete · v{newVersion}</p>
          <h1 className="canvas__display">
            {sameGrade
              ? `Grade unchanged · ${delta.gradeDelta.new}`
              : `Grade ${delta.gradeDelta.previous} → ${delta.gradeDelta.new}`}
          </h1>
          <p className="canvas__lede">
            Composite {delta.gradeDelta.previousComposite.toFixed(2)} →{" "}
            {delta.gradeDelta.newComposite.toFixed(2)} ·{" "}
            {delta.fieldsChanged.length} field
            {delta.fieldsChanged.length === 1 ? "" : "s"} changed across{" "}
            {delta.layersEdited.length} layer
            {delta.layersEdited.length === 1 ? "" : "s"}.
          </p>

          <section className="review__delta-section">
            <h2 className="review__delta-h2">Fields changed</h2>
            {delta.fieldsChanged.length === 0 ? (
              <p className="review__delta-empty">No field-level edits.</p>
            ) : (
              <ul className="review__delta-list">
                {delta.fieldsChanged.map((f, i) => (
                  <li key={`${f.phase}.${f.field}.${i}`} className="review__delta-row">
                    <div className="review__delta-head">
                      <span className="mono">
                        [{f.phase}] {f.fieldLabel}
                      </span>
                      <span className="mono">
                        {f.previousScore} → {f.newScore}
                      </span>
                    </div>
                    <div className="review__delta-before">
                      <strong>Before:</strong> {truncate(f.previousAnswer)}
                    </div>
                    <div className="review__delta-after">
                      <strong>After:</strong> {truncate(f.newAnswer)}
                    </div>
                    {(f.previousGapFlagged !== f.newGapFlagged ||
                      f.previousGapNote !== f.newGapNote) && (
                      <div className="review__delta-gap">
                        <strong>Gap:</strong>{" "}
                        {f.previousGapFlagged ? "flagged" : "none"} →{" "}
                        {f.newGapFlagged ? "flagged" : "none"}
                        {f.newGapFlagged && f.newGapNote
                          ? ` — ${f.newGapNote}`
                          : ""}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="review__delta-section">
            <h2 className="review__delta-h2">Consistency check changes</h2>
            {delta.consistencyDelta.length === 0 ? (
              <p className="review__delta-empty">No verdict changes.</p>
            ) : (
              <ul className="review__delta-cons">
                {delta.consistencyDelta.map((c) => (
                  <li key={c.code}>
                    <span className="mono">{c.code}</span> {c.label}:{" "}
                    <span className="mono">
                      {c.previousVerdict.toUpperCase()}
                    </span>{" "}
                    →{" "}
                    <span className="mono">{c.newVerdict.toUpperCase()}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {error && <div className="banner banner--error">{error}</div>}

          <footer className="canvas__actions">
            <button
              className="btn btn--primary"
              onClick={() => onFinalized(newVersion)}
            >
              Done — open new version →
            </button>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="canvas">
      <div className="canvas__panel">
        <p className="canvas__eyebrow">Phase 10 — Review</p>
        <h1 className="canvas__display">Ready to finalize</h1>
        <p className="canvas__lede">
          Finalize snapshots a new version (v(n+1)) of this canvas, regenerates
          its artifacts, and writes a delta report. Cancel discards all edits.
        </p>
        {error && <div className="banner banner--error">{error}</div>}
        <footer className="canvas__actions">
          <button
            className="btn btn--text btn--danger"
            disabled={busy !== null}
            onClick={() => void handleCancel()}
          >
            {busy === "cancel" ? "Cancelling…" : "Cancel review"}
          </button>
          <button
            className="btn btn--primary"
            disabled={busy !== null}
            onClick={() => void handleFinalize()}
          >
            {busy === "finalize" ? "Finalizing…" : "Finalize as next version →"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function truncate(s: string, n = 240): string {
  if (!s) return "(empty)";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
